import { NgModule, inject } from '@angular/core';
import { RouterModule, Routes, ResolveFn } from '@angular/router';
import { AuthGuard } from './guards/auth.guard';
import { AppInitResolver } from './resolvers/app-init.resolver';
import { DashboardRootViewComponent } from './components/dashboard-root/dashboard-root-view.component';
import { RouteResolverService } from '@go-modules/services/routing/route-resolver.service';
import { LicenseManagementGuard } from './guards/license-management.guard';
import {
	ROUTE_NAMES,
	getPathTemplate
} from '@go-modules/services/router/router.service';
import { AuthResolver } from './resolvers/auth.resolver';

const resolveFoldersRoute: ResolveFn<boolean> = (route) =>
	inject(RouteResolverService).resolveFoldersRoute(route.params);

const resolveFolderRoute: ResolveFn<boolean> = (route) =>
	inject(RouteResolverService).resolveFolderRoute(route.params);

const resolveSessionRoute: ResolveFn<boolean> = (route) =>
	inject(RouteResolverService).resolveSessionRoute(route.params);

const resolveLicenseManagementRoute: ResolveFn<boolean> = (route) =>
	inject(RouteResolverService).resolveLicenseManagementRoute(route.params);

const routes: Routes = [
	{
		path: getPathTemplate(ROUTE_NAMES.AUTH),
		data: { routeName: ROUTE_NAMES.AUTH },
		resolve: { authCheck: AuthResolver },
		children: [
			{
				path: getPathTemplate(ROUTE_NAMES.AUTH_LOGIN).replace('dashboard/auth/',''),
				data: { routeName: ROUTE_NAMES.AUTH_LOGIN },
				loadComponent: () =>
					import('./components/login-wrapper/login-wrapper.component').then(
						(m) => m.LoginWrapperComponent
					)
			},
			{
				path: getPathTemplate(ROUTE_NAMES.AUTH_SIGNUP).replace('dashboard/auth/',''),
				data: { routeName: ROUTE_NAMES.AUTH_SIGNUP },
				loadComponent: () =>
					import(
						'./components/signup/dashboard-signup-wrapper/dashboard-signup-wrapper.component'
					).then((m) => m.DashboardSignupWrapperComponent)
			},
			{
				path: getPathTemplate(ROUTE_NAMES.AUTH_FORGOT).replace('dashboard/auth/',''),
				data: { routeName: ROUTE_NAMES.AUTH_FORGOT },
				loadComponent: () =>
					import(
						'@go-modules/components/auth/forgot-password/forgot-password.component'
					).then((m) => m.ForgotPasswordComponent)
			},
			{
				path: getPathTemplate(ROUTE_NAMES.AUTH_RESET).replace('dashboard/auth/',''),
				data: { routeName: ROUTE_NAMES.AUTH_RESET },
				loadComponent: () =>
					import(
						'@go-modules/components/auth/reset-password/reset-password.component'
					).then((m) => m.ResetPasswordComponent)
			},
			{
				path: getPathTemplate(ROUTE_NAMES.AUTH_COURSE_CONFIRM).replace('dashboard/auth/',''),
				data: { routeName: ROUTE_NAMES.AUTH_COURSE_CONFIRM },
				loadComponent: () =>
					import(
						'@go-modules/components/auth/group-confirm/group-confirm.component'
					).then((m) => m.GroupConfirmComponent)
			}
		]
	},
	{
		path: getPathTemplate(ROUTE_NAMES.DASHBOARD_ROOT),
		component: DashboardRootViewComponent,
		data: { routeName: ROUTE_NAMES.DASHBOARD_ROOT },
		canActivate: [AuthGuard],
		resolve: { appInit: AppInitResolver },
		children: [
			{
				path: getPathTemplate(ROUTE_NAMES.DASHBOARD_FOLDERS).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.DASHBOARD_FOLDERS },
				loadChildren: () =>
					import('./components/folders-view/folders-view.dashboard.module').then(
						(m) => m.FoldersViewDashboardModule
					),
				resolve: { foldersRouting: resolveFoldersRoute }
			},
			{
				path: getPathTemplate(ROUTE_NAMES.DASHBOARD_SESSION_VIEW).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.DASHBOARD_SESSION_VIEW },
				loadChildren: () =>
					import(
						'@go-modules/components/feedback-session/session-view/session-view.dashboard.module'
					).then((m) => m.SessionViewDashboardModule),
				resolve: { sessionRouting: resolveSessionRoute }
			},
			{
				path: getPathTemplate(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW },
				loadChildren: () =>
					import(
						'@go-modules/components/activity-view/activity-view.dashboard.module'
					).then((m) => m.ActivityViewDashboardModule)
			},
			{
				path: getPathTemplate(ROUTE_NAMES.DASHBOARD_FOLDER_VIEW).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.DASHBOARD_FOLDER_VIEW },
				loadChildren: () =>
					import('@go-modules/components/folder-view/folder-view.dashboard.module').then(
						(m) => m.FolderViewDashboardModule
					),
				resolve: { folderRouting: resolveFolderRoute }
			},
			{
				path: getPathTemplate(ROUTE_NAMES.DASHBOARD_ARCHIVE).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.DASHBOARD_ARCHIVE },
				loadChildren: () =>
					import(
						'@go-modules/components/archive/archive-view/archive-view.dashboard.module'
					).then((m) => m.ArchiveViewDashboardModule)
			},
			{
				path: getPathTemplate(ROUTE_NAMES.DASHBOARD_SETTINGS).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.DASHBOARD_SETTINGS },
				loadChildren: () =>
					import(
						'@go-modules/components/user-settings/user-settings.dashboard.module'
					).then((m) => m.UserSettingsDashboardModule)
			},
			{
				path: getPathTemplate(ROUTE_NAMES.LICENSE_MANAGEMENT).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.LICENSE_MANAGEMENT },
				loadChildren: () =>
					import(
						'@go-modules/components/license-management/license-management.dashboard.module'
					).then((m) => m.LicenseManagementDashboardModule),
				canActivate: [LicenseManagementGuard],
				resolve: { routeResolver: resolveLicenseManagementRoute }
			},
			{
				path: getPathTemplate(ROUTE_NAMES.DASHBOARD_LICENSE_MANAGEMENT_ORG).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.DASHBOARD_LICENSE_MANAGEMENT_ORG },
				loadChildren: () =>
					import(
						'@go-modules/components/license-management/license-management.dashboard.module'
					).then((m) => m.LicenseManagementDashboardModule),
				canActivate: [LicenseManagementGuard]
			},
			{
				path: getPathTemplate(ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.LICENSE_SEATS_MANAGEMENT },
				loadChildren: () =>
					import(
						'@go-modules/components/license-seats-management/license-seats-management.dashboard.module'
					).then((m) => m.LicenseSeatsManagementDashboardModule),
				canActivate: [LicenseManagementGuard]
			},
			{
				path: getPathTemplate(ROUTE_NAMES.DASHBOARD_NO_GROUPS).replace('dashboard/',''),
				data: { routeName: ROUTE_NAMES.DASHBOARD_NO_GROUPS },
				loadChildren: () =>
					import('@go-modules/components/no-groups/no-groups.dashboard.module').then(
						(m) => m.NoGroupsDashboardModule
					)
			}
		]
	},
	{
		path: '',
		redirectTo: getPathTemplate(ROUTE_NAMES.AUTH_LOGIN),
		pathMatch: 'full'
	},
	{
		path: '**',
		redirectTo: getPathTemplate(ROUTE_NAMES.AUTH_LOGIN)
	}
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule]
})
export class AppRoutingModule {}
